'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { MonitoringData, GraphPoint } from '@/types';

export const useMonitoringGraph = () => {
  const [data, setData] = useState<MonitoringData>({
    points: [],
    currentValue: 98.7,
    trend: 'stable'
  });

  const maxPoints = 50;
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Start real-time updates with stable interval
  useEffect(() => {
    const updateData = () => {
      setData(prevData => {
        // If no data points yet, initialize them
        if (prevData.points.length === 0) {
          const points: GraphPoint[] = [];
          const now = Date.now();

          for (let i = 0; i < maxPoints; i++) {
            // Inline fun value generation to avoid dependency
            const baseValue = 92;
            const variation = Math.sin(i * 0.3) * 3; // Gentle fun waves
            const noise = (Math.random() - 0.5) * 2; // Chaotic fun energy
            const trend = Math.sin(i * 0.05) * 5; // Long-term fun trends
            const value = Math.max(85, Math.min(99, baseValue + variation + noise + trend));

            const timestamp = now - (maxPoints - i - 1) * 2000; // 2 seconds per point

            points.push({
              x: 0, // Will be calculated in path generation
              y: 0, // Will be calculated in path generation
              value,
              timestamp
            });
          }

          return {
            points,
            currentValue: points[points.length - 1]?.value || 98.7,
            trend: 'stable'
          };
        }

        // Update existing data
        const newPoints = [...prevData.points];

        // Remove oldest point and add new one
        newPoints.shift();
        // Inline fun value generation to avoid dependency
        const baseValue = 92;
        const variation = Math.sin((Date.now() / 1000) * 0.3) * 3; // Gentle fun waves
        const noise = (Math.random() - 0.5) * 2; // Chaotic fun energy
        const trendFactor = Math.sin((Date.now() / 1000) * 0.05) * 5; // Long-term fun trends
        const newValue = Math.max(85, Math.min(99, baseValue + variation + noise + trendFactor));

        newPoints.push({
          x: 0, // Will be calculated in path generation
          y: 0, // Will be calculated in path generation
          value: newValue,
          timestamp: Date.now()
        });

        // Determine trend based on last few points
        const recentPoints = newPoints.slice(-3);
        const firstValue = recentPoints[0]?.value || 0;
        const lastValue = recentPoints[recentPoints.length - 1]?.value || 0;
        const diff = lastValue - firstValue;

        let trendDirection: 'up' | 'down' | 'stable' = 'stable';
        if (Math.abs(diff) > 0.2) {
          trendDirection = diff > 0 ? 'up' : 'down';
        }

        return {
          points: newPoints,
          currentValue: newValue,
          trend: trendDirection
        };
      });
    };

    // Initialize immediately
    updateData();

    // Update every 2 seconds for smooth real-time feel
    intervalRef.current = setInterval(updateData, 2000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []); // Empty dependency array - only set up once

  // Generate SVG path with cubic Bézier curves for smooth transitions
  const generateSvgPath = useCallback((points: GraphPoint[], width: number = 400, height: number = 150): { path: string; processedPoints: GraphPoint[] } => {
    if (points.length === 0) return { path: '', processedPoints: [] };

    const padding = 10;
    const graphHeight = height - padding;
    const processedPoints: GraphPoint[] = [];

    // Calculate positions for each point
    for (let i = 0; i < points.length; i++) {
      const x = (i / (points.length - 1)) * width;
      // Scale 85-99% to available height with padding at top
      const y = padding + (graphHeight - ((points[i].value - 85) / 14) * graphHeight);

      processedPoints.push({
        ...points[i],
        x,
        y
      });
    }

    if (processedPoints.length === 0) return { path: '', processedPoints: [] };

    // Start the path
    let pathData = `M ${processedPoints[0].x} ${processedPoints[0].y}`;

    // Add cubic Bézier curves for smooth transitions
    for (let i = 1; i < processedPoints.length; i++) {
      const cp1x = processedPoints[i - 1].x + (processedPoints[i].x - processedPoints[i - 1].x) / 3;
      const cp1y = processedPoints[i - 1].y;
      const cp2x = processedPoints[i].x - (processedPoints[i].x - processedPoints[i - 1].x) / 3;
      const cp2y = processedPoints[i].y;

      pathData += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${processedPoints[i].x} ${processedPoints[i].y}`;
    }

    return { path: pathData, processedPoints };
  }, []);

  // Generate SVG path for the filled area under the curve
  const generateAreaPath = useCallback((points: GraphPoint[], width: number = 400, height: number = 150): string => {
    const { path: linePath } = generateSvgPath(points, width, height);
    if (!linePath) return '';

    if (points.length === 0) return '';

    // Calculate the actual x positions for first and last points
    const firstX = 0;
    const lastX = width;

    // Close the path by going to bottom-right, then bottom-left, then back to start
    return `${linePath} L ${lastX} ${height} L ${firstX} ${height} Z`;
  }, [generateSvgPath]);

  return {
    data,
    generateSvgPath,
    generateAreaPath
  };
};
